<script setup>
// Admin upload order hộ khách hàng (màn Customer).
// Copy từ orders/modal/importOrder.vue, chỉ thêm gửi user_id của khách — không sửa modal gốc của màn Order.
import { ref } from 'vue'
import { createToaster } from "@meforma/vue-toaster";
import { useOrders } from "@/composables/useOrders";
import { useApi } from "@/composables/useApi";

const toaster = createToaster({
  position: "top-right",
  duration: 2000,
});

const props = defineProps({
  isOpen: Boolean,
  customer: { type: Object, default: null }
})

const emit = defineEmits(['close', 'success'])

const { downloadTemplateApi } = useOrders();
const { postData } = useApi();
const loading = ref(false)
const downloading = ref(false)
const selectedFile = ref(null)
const fileInput = ref(null)

const closeForm = () => {
  if (loading.value || downloading.value) return;
  selectedFile.value = null;
  if (fileInput.value) fileInput.value.value = '';
  emit('close')
}

const handleFileChange = (event) => {
  const file = event.target.files[0];
  if (file) {
    selectedFile.value = file;
  }
}

const handleDownloadTemplate = async () => {
  downloading.value = true;
  try {
    const blob = await downloadTemplateApi();

    if (blob instanceof Blob) {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'order_import_template.xlsx');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toaster.success("Tải template thành công!");
    } else {
      toaster.error("Không thể tải template");
    }
  } catch (err) {
    toaster.error("Có lỗi xảy ra khi tải template");
  } finally {
    downloading.value = false;
  }
};

const handleSubmit = async () => {
  if (!selectedFile.value) {
    toaster.error("Vui lòng chọn file!");
    return;
  }
  if (!props.customer?.id) {
    toaster.error("Không xác định được khách hàng!");
    return;
  }

  loading.value = true;
  try {
    const formData = new FormData();
    formData.append('import_file', selectedFile.value);
    formData.append('user_id', props.customer.id);
    await postData('/api/orders/import', formData);
    toaster.success("Import thành công!");
    emit('success');
    closeForm();
  } catch (err) {
    toaster.error(err.message || "Import thất bại");
  } finally {
    loading.value = false;
  }
};
</script>

<template>
  <div v-if="isOpen" class="form-sidebar-overlay" @click.self="closeForm">
    <div class="form-sidebar">
      <div class="d-flex justify-content-between align-items-center mb-4">
        <h5 class="mb-0">Import Orders</h5>
        <button class="btn-close" :disabled="loading || downloading" @click="closeForm"></button>
      </div>

      <div v-if="customer" class="alert alert-warning py-2 mb-4">
        <small>
          Import order cho khách hàng:
          <strong>{{ customer.name }}</strong>
          <span v-if="customer.email"> ({{ customer.email }})</span>
          - ID {{ customer.id }}
        </small>
      </div>

      <form @submit.prevent="handleSubmit">
        <div class="mb-4">
          <label class="form-label fw-bold">Chọn file Excel</label>
          <input
            type="file"
            class="form-control"
            accept=".xlsx, .xls, .csv"
            ref="fileInput"
            @change="handleFileChange"
            :disabled="loading || downloading"
          >
          <div class="form-text mt-2">
            Hỗ trợ các định dạng .xlsx, .xls
          </div>
        </div>

        <div v-if="selectedFile" class="alert alert-info py-2">
          <small>File đã chọn: {{ selectedFile.name }}</small>
        </div>

        <div class="sidebar-footer d-flex flex-column gap-2 mt-auto">
          <div class="d-flex gap-2">
            <button
              type="button"
              class="btn btn-outline-secondary w-100"
              :disabled="loading || downloading"
              @click="closeForm"
            >
              Hủy
            </button>
            <button
              type="submit"
              class="btn btn-primary w-100"
              :disabled="loading || downloading || !selectedFile"
            >
              <span v-if="loading" class="spinner-border spinner-border-sm me-1"></span>
              {{ loading ? 'Đang xử lý...' : 'Import' }}
            </button>
          </div>

          <button
            type="button"
            class="btn btn-outline-info w-100"
            :disabled="loading || downloading"
            @click="handleDownloadTemplate"
          >
            <span v-if="downloading" class="spinner-border spinner-border-sm me-1"></span>
            <i class="fa fa-download me-1"></i> Download excel template
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<style scoped>
.form-sidebar-overlay {
  position: fixed;
  top: 0; left: 0;
  width: 100%; height: 100%;
  background: rgba(0,0,0,0.3);
  z-index: 9999;
}

.form-sidebar {
  position: fixed;
  top: 0; right: 0;
  width: 400px; height: 100%;
  background: white;
  padding: 25px;
  box-shadow: -5px 0 15px rgba(0,0,0,0.1);
  display: flex;
  flex-direction: column;
  animation: slideIn 0.3s ease-out;
}

.sidebar-footer {
  margin-top: auto;
  padding-top: 20px;
}

@keyframes slideIn {
  from { transform: translateX(100%); }
  to { transform: translateX(0); }
}

.btn:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}
</style>
